import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ScannerConnectionStatus } from '@rtkwlf/rendall';
import type { EditScannerConfigInput } from '@rtkwlf/rendall';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Stack } from '@packages/fenrir/components';
import { EventKey } from 'ae-constants/constants';

import type { ScannerDetail } from '../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import { ScannerConfigFormControls } from '../scanners-config/components/scanner-configuration-tabs/scanner-config-form-controls/scanner-config-form-controls';
import { useScannerConfigurationContext } from '../scanners-config/components/scanner-configuration-tabs/scanner-configuration-context-provider';
import { HostDnsServers } from '../scanners-config/components/scanner-configuration-tabs/tabs/host-dns-servers/host-dns-servers';
import { useEditScannerConfigWrapper } from '../scanners-config/components/scanner-configuration-tabs/use-edit-scanner-config-wrapper';
import { useScannerConfigValidationSchema } from '../scanners-config/components/scanner-configuration-tabs/use-scanner-config-validation-schema/use-scanner-config-validation-schema';

export const HostDnsServersForm: React.FC<{
  scannerDetails: ScannerDetail;
}> = ({ scannerDetails }) => {
  const { scannerId = '' } = useParams();
  const { setIsFormDirty } = useScannerConfigurationContext();

  const validationSchema = useScannerConfigValidationSchema();

  const { mutate: editScannerConfig, isPending: updatePending } =
    useEditScannerConfigWrapper(scannerId);

  const {
    control,
    setValue,
    formState: { isDirty, isValid, errors },
    reset,
    getValues,
    handleSubmit,
  } = useForm<EditScannerConfigInput>({
    defaultValues: {
      hostIdenServers: scannerDetails.dnsServers?.sort(),
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => setIsFormDirty(isDirty), [isDirty, setIsFormDirty]);

  const onSubmit = () => {
    const data = {
      name: scannerDetails.name,
      hostIden: scannerDetails.hostIdentificationScans,
      vulnScan: scannerDetails.vulnerabilityScans,
      cgiScan: scannerDetails.CGIScanning,
      bruteScan: scannerDetails.bruteForceChecks,
      pingOnly: scannerDetails.onlyPingTheTarget,
      hostIdenServers: getValues('hostIdenServers')?.sort(),
      scanExclusionList: scannerDetails.denyList?.sort(),
    };
    editScannerConfig(data, {
      onSuccess: () => {
        reset(data);
      },
    });
  };

  const isSubmitDisabled =
    scannerDetails?.connectionStatus !== ScannerConnectionStatus.Connected ||
    !isDirty ||
    !isValid;

  const preventSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === EventKey.Return) e.preventDefault();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventSubmitOnEnter}>
      <Stack gap={3}>
        <HostDnsServers
          control={control}
          setValue={setValue}
          errors={errors}
          getValues={getValues}
        />
        <ScannerConfigFormControls
          isDirty={isDirty}
          isDisabled={isSubmitDisabled}
          isPending={updatePending}
          reset={reset}
        />
      </Stack>
    </form>
  );
};
